import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Tabs,
  Tab,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ExpandMore as ExpandMoreIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  AttachFile as AttachFileIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  DataUsage as DataUsageIcon,
  BugReport as BugReportIcon,
  LockOpen as LockOpenIcon,
  Videocam as VideocamIcon,
  Image as ImageIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Psychology as PsychologyIcon,
  Visibility as VisibilityIcon,
  Gavel as GavelIcon,
  Block as BlockIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
} from '@mui/icons-material';
import prologVideo from '../assets/0 - Prolog.mp4';
import fakeShopVideo from '../assets/1- Fake Shops.mp4';
import phishingVideo from '../assets/2 - Phishing.mp4';
import credentialsVideo from '../assets/3 - Credential Loss.mp4';
import aiAttacksVideo from '../assets/4 - AI Attacks.mp4';
import schockAnrufVideo from '../assets/5 - Schock Anruf.mp4';
import epilogVideo from '../assets/6 - Epilog.mp4';

// Section mapping
const sections = [
  { id: 'intro', label: 'Intro', path: 'intro', number: 1 },
  { id: 'fake-shop', label: 'Fake-Shop', path: 'fake-shop', number: 2 },
  { id: 'phishing', label: 'Phishing', path: 'phishing', number: 3 },
  { id: 'credentials', label: 'Zugangsdaten', path: 'credentials', number: 4 },
  { id: 'ai-attacks', label: 'Angriffe mit KI', path: 'ai-attacks', number: 5 },
  { id: 'schock-anrufe', label: 'Schock-Anrufe', path: 'schock-anrufe', number: 6 },
  { id: 'epilog', label: 'Fazit', path: 'epilog', number: 7 },
];

// Helper function to create chapter layout with video, optional Merksatz and accordion
const ChapterLayout = ({ title, video, accordionTitle, accordionItems, noteTitle, noteText }) => {
  return (
    <Grid item xs={12} sx={{ mb: 2 }}>
      <Paper elevation={3} sx={{ 
        p: 4, 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderLeft: '6px solid #2196f3',
        color: '#ffffff', 
        borderRadius: 2,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(10px)',
      }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
          {title}
        </Typography>
        
        {/* Centered video */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Box
            component="video"
            src={video}
            controls
            sx={{
              width: '100%',
              maxWidth: '450px',
              height: 'auto',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
            }}
          />
        </Box>
        
        {/* Optional Merksatz / Hinweisbox über dem Accordion */}
        {noteTitle && noteText && (
          <Box sx={{ mt: 2 }}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)',
              border: '2px solid rgba(76, 175, 80, 0.3)',
              borderLeft: '6px solid #4caf50',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(76, 175, 80, 0.2)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <InfoIcon sx={{ color: '#4caf50', fontSize: 32, mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    {noteTitle}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.8 }}>
                    {noteText}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Expandable section */}
        <Box sx={{ mt: 4 }}>
          <Accordion
            sx={{
              background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              '&:before': {
                display: 'none',
              },
              '&.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#42a5f5' }} />}
              sx={{
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 0.15)',
                },
                '& .MuiAccordionSummary-content': {
                  margin: '16px 0',
                },
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff' }}>
                {accordionTitle}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 3, pb: 3 }}>
              <List sx={{ color: '#ffffff' }}>
                {accordionItems.map((item, index) => (
                  <ListItem key={index} sx={{ py: 1.5 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {item.description}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Paper>
    </Grid>
  );
};

// Individual Section Component
const StorySection = ({ sectionId, sectionNumber, sectionLabel }) => {
  // Special content for Intro section
  if (sectionId === 'intro') {
    return (
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Paper elevation={3} sx={{ 
          p: 4, 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderLeft: '6px solid #2196f3',
          color: '#ffffff', 
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
        }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
            "Mir passiert das doch nicht" – 
          </Typography>
          <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
            Evelyns Weg durch die Cyberfallen
          </Typography>
          <Typography variant="h6" component="h2" sx={{ mb: 4, color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'italic', fontWeight: 400, textAlign: 'center' }}>
            Eine reale Online-Geschichte über Dinge, die „nur den anderen" passieren – bis es einen plötzlich selbst trifft.
          </Typography>
          
          {/* Centered video above columns */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              component="video"
              src={prologVideo}
              controls
              sx={{
                width: '100%',
                maxWidth: '450px',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              }}
            />
          </Box>
          
          {/* Two-column layout */}
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {/* Left column: Teaser text */}
            <Grid item xs={12} md={6}>
              <Typography variant="body1" sx={{ color: '#ffffff', lineHeight: 1.8, fontSize: '1.1rem' }}>
                <strong>Evelyn ist kein Computer-Profi.</strong>
              </Typography>
              <Typography variant="body1" sx={{ color: '#ffffff', lineHeight: 1.8, mt: 2 }}>
                Sie shoppt online, chattet, macht ein wenig Social Media und nutzt Online-Banking – so wie fast alle.
              </Typography>
              <Typography variant="body1" sx={{ color: '#ffffff', lineHeight: 1.8, mt: 2 }}>
                Ihre Geschichte zeigt, wie man ganz nebenbei in Cyberfallen geraten kann – und wie wir uns im Alltag besser schützen können.
              </Typography>
            </Grid>
            
            {/* Right column: 4 bullet points with icons */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Typography variant="h5" sx={{ lineHeight: 1, mt: 0.5 }}>🛒</Typography>
                  <Box>
                    <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                      Online-Einkauf: Fakeshops & Schnäppchenfallen
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Typography variant="h5" sx={{ lineHeight: 1, mt: 0.5 }}>📧</Typography>
                  <Box>
                    <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                      Phishing: moderne Betrugs-Mails & Nachrichten
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Typography variant="h5" sx={{ lineHeight: 1, mt: 0.5 }}>🤖</Typography>
                  <Box>
                    <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                      KI-Fälschungen: gefälschte Stimmen, Bilder & „News"
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Typography variant="h5" sx={{ lineHeight: 1, mt: 0.5 }}>📞</Typography>
                  <Box>
                    <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                      Schockanrufe: emotionale Erpressung am Telefon
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    );
  }

  // Special content for Fake-Shop section
  if (sectionId === 'fake-shop') {
    const fakeShopItems = [
      {
        icon: <AttachMoneyIcon sx={{ color: '#ff9800' }} />,
        title: 'Unrealistisch niedrige Preise & "Dauer Sale"',
        description: 'Wenn ein Produkt deutlich günstiger ist als bei seriösen Händlern, ist Vorsicht geboten.',
        whatToDo: 'Vergleichen Sie Preise bei mehreren seriösen Händlern. Wenn ein Angebot zu gut erscheint, um wahr zu sein, ist es das meist auch.'
      },
      {
        icon: <BusinessIcon sx={{ color: '#2196f3' }} />,
        title: 'Fehlende oder unvollständige Impressumsangaben',
        description: 'Seriöse Shops müssen ein vollständiges Impressum mit Anschrift und Kontaktdaten haben.',
        whatToDo: 'Prüfen Sie das Impressum auf Vollständigkeit. Fehlt es, wirkt es unvollständig oder steht da nur „Kontaktformular / Gmail / WhatsApp“ sollten Sie vom Kauf absehen. '
      },
      {
        icon: <PaymentIcon sx={{ color: '#4caf50' }} />,
        title: 'Ungewöhnliche Zahlungsmethoden',
        description: 'Vorsicht bei Vorkasse, Überweisung oder ungewöhnlichen Zahlungsanbietern ohne Käuferschutz.',
        whatToDo: 'Nutzen Sie Zahlungsmethoden mit Käuferschutz wie PayPal, Kreditkarte oder Lastschrift. Vermeiden Sie Vorkasse bei unbekannten Händlern.'
      },
      {
        icon: <SearchIcon sx={{ color: '#00bcd4' }} />,
        title: 'Fehlende oder verdächtige Bewertungen',
        description: 'Fake-Shops haben oft keine oder nur sehr wenige Bewertungen. Oder die vorhandenen Bewertungen wirken gefälscht oder unplausibel.',
        whatToDo: 'Suchen Sie online nach Erfahrungsberichten oder Bewertungen zu diesem Shop. Nutzen Sie Suchmaschinen mit dem Shop-Namen und achten Sie auf mehrere unabhängige Treffer von verschiedenen Quellen.'
      },
      {
        icon: <WarningIcon sx={{ color: '#ff5722' }} />,
        title: 'Bauchgefühl-Check im Checkout',
        description: 'Werden ungewöhnlich viele Daten verlangt (Geburtsdatum ohne Grund, Ausweis, unnötige Infos)? Gibt es keine klare Widerrufs-/Rückgabeinfos oder nur sehr vage Texte?',
        whatToDo: 'Vertrauen Sie auf Ihr Bauchgefühl. Wenn etwas beim Checkout-Prozess merkwürdig oder ungewöhnlich wirkt, brechen Sie den Kauf ab. Seriöse Händler verlangen nur notwendige Daten und informieren klar über Ihre Rechte.'
      }
    ];

    return (
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Paper elevation={3} sx={{ 
          p: 4, 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderLeft: '6px solid #2196f3',
          color: '#ffffff', 
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
        }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
            Gefährliche Schnäppchen: Online-Einkauf mit Risiko
          </Typography>
          
          {/* Centered video */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              component="video"
              src={fakeShopVideo}
              controls
              sx={{
                width: '100%',
                maxWidth: '450px',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              }}
            />
          </Box>

          {/* Demo links */}
          {/* <Box sx={{ mb: 4 }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(0, 188, 212, 0.12) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
              }}
            >
              <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700, mb: 2, textAlign: 'center' }}>
                Demo
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  href="https://electrokaufen.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.25)',
                  }}
                >
                  electrokaufen.de
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  href="https://www.fahrrad-sparwelt.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.25)',
                  }}
                >
                  fahrrad-sparwelt.de
                </Button>
              </Box>
            </Paper>
          </Box> */}
          
          {/* Merkregel über den Erkennungsmerkmalen */}
          <Box sx={{ mt: 2 }}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)',
              border: '2px solid rgba(76, 175, 80, 0.3)',
              borderLeft: '6px solid #4caf50',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(76, 175, 80, 0.2)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <InfoIcon sx={{ color: '#4caf50', fontSize: 32, mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    Merkregel
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.8 }}>
                    <strong>Ab drei oder mehr Warnsignalen:</strong> Lieber nicht kaufen. Falls doch, nur mit entsprechendem Käuferschutz.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Expandable section with icons */}
          <Box sx={{ mt: 4 }}>
            <Accordion
              defaultExpanded={false}
              sx={{
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#42a5f5' }} />}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff' }}>
                  Typische Erkennungsmerkmale für einen Fake-Shop
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3 }}>
                <List sx={{ color: '#ffffff' }}>
                  {fakeShopItems.map((item, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        py: 2,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48, mr: 2 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, mb: 1.5 }}>
                              {item.description}
                            </Typography>
                            {item.whatToDo && (
                              <Box sx={{ 
                                mt: 1.5, 
                                pt: 1.5, 
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                borderRadius: 1,
                                p: 1.5,
                              }}>
                                <Typography variant="body2" sx={{ color: '#42a5f5', fontWeight: 600, mb: 0.5 }}>
                                  Was können Sie tun?
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                                  {item.whatToDo}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Paper>
      </Grid>
    );
  }

  // Special content for Phishing section
  if (sectionId === 'phishing') {
    const phishingItems = [
      {
        icon: <ScheduleIcon sx={{ color: '#f44336' }} />,
        title: 'Ungewöhnlicher Druck + Dringlichkeit',
        description: 'Phishing-Nachrichten erzeugen künstlichen Druck: "Sofort handeln", "Konto wird gesperrt", "nur heute", "letzte Warnung". Das Ziel ist, dass Sie nicht nachdenken, sondern sofort klicken oder etwas ausführen.',
        whatToDo: 'Lassen Sie sich nicht unter Druck setzen. Seriöse Unternehmen geben Ihnen Zeit für wichtige Entscheidungen. Nehmen Sie sich die Zeit, die Nachricht genau zu prüfen, bevor Sie handeln.'
      },
      {
        icon: <EmailIcon sx={{ color: '#2196f3' }} />,
        title: 'Der Absender passt nicht zur Situation',
        description: 'Der Absendername sieht okay aus, aber die E-Mail-Adresse oder Domäne ist komisch (z. B. support@paypaI-hilfe.com statt paypal.com). Bei ClickFix auch häufig: Nachricht kommt aus unerwarteter Richtung (z. B. "IT-Support" per WhatsApp/Teams, obwohl ihr das nie so macht).',
        whatToDo: 'Prüfen Sie immer die vollständige E-Mail-Adresse oder Absender-Informationen. Seriöse Unternehmen nutzen ihre offiziellen Domains. Bei ungewöhnlichen Kommunikationskanälen sollten Sie misstrauisch sein.'
      },
      {
        icon: <LinkIcon sx={{ color: '#ff9800' }} />,
        title: 'Link-Tricks oder "falsche" Login-Seiten',
        description: 'Der Linktext sagt "Microsoft/Bank", aber wenn Sie mit der Maus drüberfahren (oder am Handy lange drücken), sehen Sie eine andere Adresse. Die Login-Seite wirkt "fast echt", aber die Domain ist nicht die offizielle (oft mit Zusatzwörtern: secure-…, verify-…, login-…).',
        whatToDo: 'Prüfen Sie Links immer, bevor Sie darauf klicken. Fahren Sie mit der Maus über den Link oder drücken Sie lange auf dem Handy, um die echte URL zu sehen. Geben Sie Login-Daten nur auf offiziellen Domains ein.'
      },
      {
        icon: <AttachFileIcon sx={{ color: '#9c27b0' }} />,
        title: 'Unerwartete Datei / Aufforderung etwas auszuführen',
        description: 'Anhänge wie Rechnung.zip, Dokument.html, PDF.exe, Office-Dateien mit "Makros aktivieren". ClickFix-typisch: "Öffne Terminal/PowerShell", "führe diesen Befehl aus", "kopiere diesen Code", "installiere dieses Tool" – das ist ein riesiges Warnsignal.',
        whatToDo: 'Öffnen Sie niemals unerwartete Anhänge, besonders .exe, .zip oder Office-Dateien mit Makros. Führen Sie keine Befehle aus, die Sie per Nachricht erhalten haben. Seriöse IT-Support-Teams würden nie per Nachricht nach solchen Aktionen fragen.'
      },
      {
        icon: <SecurityIcon sx={{ color: '#4caf50' }} />,
        title: 'Ungewöhnliche Datenabfrage',
        description: 'Abfrage nach Passwort, 2FA-Code, Kreditkartendaten, Recovery-Codes, "Bestätige kurz deinen Code". Seriöse Firmen/IT fragen Sie in der Regel nicht nach Ihrem Passwort oder 2FA-Code.',
        whatToDo: 'Geben Sie niemals Passwörter, 2FA-Codes, Kreditkartendaten oder Recovery-Codes per Nachricht preis. Seriöse Unternehmen würden nie danach fragen. Wenn Sie unsicher sind, kontaktieren Sie das Unternehmen direkt über offizielle Kanäle.'
      }
    ];

    return (
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Paper elevation={3} sx={{ 
          p: 4, 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderLeft: '6px solid #2196f3',
          color: '#ffffff', 
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
        }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
            Betrügerische Nachrichten: Phishing erkennen und abwehren
          </Typography>
          
          {/* Centered video */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              component="video"
              src={phishingVideo}
              controls
              sx={{
                width: '100%',
                maxWidth: '450px',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              }}
            />
          </Box>

          {/* Demo section with image */}
          {/* <Box sx={{ mb: 4 }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(0, 188, 212, 0.12) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h5"
                sx={{ color: '#ffffff', fontWeight: 700, mb: 2 }}
              >
                Demo
              </Typography>
              <Box
                component="img"
                src=""
                alt="Phishing Demo: Shoeshop"
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
                  mx: 'auto',
                  display: 'block',
                }}
              />
            </Paper>
          </Box> */}
          
          {/* Merksatz über den Erkennungsmerkmalen */}
          <Box sx={{ mt: 2 }}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)',
              border: '2px solid rgba(76, 175, 80, 0.3)',
              borderLeft: '6px solid #4caf50',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(76, 175, 80, 0.2)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <InfoIcon sx={{ color: '#4caf50', fontSize: 32, mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    Merksatz
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.8 }}>
                    <strong>Bei verdächtigen Nachrichten:</strong> Immer zuerst prüfen, dann handeln. Bei Unsicherheit lieber direkt beim Unternehmen nachfragen, bevor Sie auf Links klicken oder Daten eingeben.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Expandable section with icons */}
          <Box sx={{ mt: 4 }}>
            <Accordion
              defaultExpanded={false}
              sx={{
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#42a5f5' }} />}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff' }}>
                  Typische Erkennungsmerkmale für Phishing
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3 }}>
                <List sx={{ color: '#ffffff' }}>
                  {phishingItems.map((item, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        py: 2,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48, mr: 2 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, mb: 1.5 }}>
                              {item.description}
                            </Typography>
                            {item.whatToDo && (
                              <Box sx={{ 
                                mt: 1.5, 
                                pt: 1.5, 
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                borderRadius: 1,
                                p: 1.5,
                              }}>
                                <Typography variant="body2" sx={{ color: '#42a5f5', fontWeight: 600, mb: 0.5 }}>
                                  Was können Sie tun?
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                                  {item.whatToDo}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Paper>
      </Grid>
    );
  }

  // Special content for Credentials section
  if (sectionId === 'credentials') {
    const credentialsItems = [
      {
        icon: <LinkIcon sx={{ color: '#f44336' }} />,
        title: 'Phishing-Angriffe',
        description: 'Betrüger täuschen Login-Seiten vor, um Passwörter zu stehlen. Sie senden gefälschte E-Mails oder Nachrichten, die zu gefälschten Websites führen, die wie echte Login-Seiten aussehen.',
        whatToDo: 'Wenn Sie auf eine Phishing-Seite geklickt und Ihr Passwort eingegeben haben: Ändern Sie das Passwort sofort auf der echten Website. Aktivieren Sie 2FA, falls noch nicht geschehen. Prüfen Sie Ihre Konten auf verdächtige Aktivitäten. Melden Sie den Vorfall dem betroffenen Unternehmen.'
      },
      {
        icon: <DataUsageIcon sx={{ color: '#2196f3' }} />,
        title: 'Datenlecks bei Unternehmen',
        description: 'Selbst bei sicheren Passwörtern können Datenlecks bei Unternehmen Ihre Zugangsdaten preisgeben. Hacker erbeuten große Datenbanken mit Millionen von E-Mail-Adressen und Passwörtern.',
        whatToDo: 'Prüfen Sie regelmäßig auf haveibeenpwned.com, ob Ihre E-Mail-Adresse in einem Leak war. Wenn ja: Ändern Sie sofort das betroffene Passwort. Nutzen Sie ein einzigartiges Passwort für jedes Konto. Aktivieren Sie 2FA für zusätzlichen Schutz.'
      },
      {
        icon: <VpnKeyIcon sx={{ color: '#ff9800' }} />,
        title: 'Passwort-Wiederverwendung',
        description: 'Viele Nutzer verwenden dasselbe Passwort für mehrere Konten. Wenn ein Passwort in einem Leak auftaucht, können Angreifer es auf anderen Websites ausprobieren und so Zugriff auf alle Ihre Konten erhalten.',
        whatToDo: 'Wenn Sie dasselbe Passwort mehrfach verwendet haben: Ändern Sie alle betroffenen Passwörter sofort. Verwenden Sie für jedes Konto ein einzigartiges, starkes Passwort. Nutzen Sie einen Passwort-Manager, um verschiedene Passwörter zu verwalten. Aktivieren Sie 2FA überall, wo möglich.'
      },
      {
        icon: <LockOpenIcon sx={{ color: '#9c27b0' }} />,
        title: 'Schwache Passwörter',
        description: 'Einfache Passwörter wie "123456", "Passwort" oder persönliche Informationen können in Sekunden geknackt werden. Angreifer nutzen Wörterbücher und Brute-Force-Angriffe, um schwache Passwörter zu erraten.',
        whatToDo: 'Wenn Sie ein schwaches Passwort verwendet haben: Ändern Sie es sofort zu einem starken Passwort (mindestens 12 Zeichen, Groß- und Kleinbuchstaben, Zahlen, Sonderzeichen). Verwenden Sie keine persönlichen Informationen. Nutzen Sie einen Passwort-Manager für sichere, zufällige Passwörter. Aktivieren Sie 2FA.'
      },
      {
        icon: <BugReportIcon sx={{ color: '#4caf50' }} />,
        title: 'Keylogger und Malware',
        description: 'Schadsoftware kann Tastatureingaben aufzeichnen und Passwörter stehlen. Keylogger werden oft über infizierte E-Mail-Anhänge, Downloads oder USB-Sticks auf Ihren Computer gebracht.',
        whatToDo: 'Wenn Sie vermuten, dass Malware auf Ihrem Gerät ist: Führen Sie sofort einen vollständigen Virenscan durch. Ändern Sie alle Passwörter von einem sauberen, sicheren Gerät aus. Prüfen Sie Ihre Konten auf verdächtige Aktivitäten. Installieren Sie Sicherheitsupdates und halten Sie Ihre Software aktuell. Erwägen Sie eine Neuinstallation des Betriebssystems bei schweren Infektionen.'
      }
    ];

    return (
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Paper elevation={3} sx={{ 
          p: 4, 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderLeft: '6px solid #2196f3',
          color: '#ffffff', 
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
        }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
            Gestohlene Zugangsdaten: Wenn Passwörter in falsche Hände geraten
          </Typography>
          
          {/* Centered video */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              component="video"
              src={credentialsVideo}
              controls
              sx={{
                width: '100%',
                maxWidth: '450px',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              }}
            />
          </Box>

          {/* Demo section */}
          {/* <Box sx={{ mb: 4 }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(0, 188, 212, 0.12) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
              }}
            >
              <Typography
                variant="h5"
                sx={{ color: '#ffffff', fontWeight: 700, mb: 2 }}
              >
                Demo
              </Typography>
              <List sx={{ color: '#ffffff', mb: 0 }}>
                <ListItem
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, mr: 1.5 }}>
                    <BugReportIcon sx={{ color: '#ff9800' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                        Stealer-Malware
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                        Schadsoftware, die gezielt gespeicherte Zugangsdaten aus Browsern, Passwort-Managern oder Apps ausliest
                        und heimlich an Angreifer überträgt – oft nach einem unbedachten Klick auf einen Anhang oder Download.
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Paper>
          </Box> */}

          {/* Merkregel über den Erkennungsmerkmalen */}
          <Box sx={{ mt: 2 }}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)',
              border: '2px solid rgba(76, 175, 80, 0.3)',
              borderLeft: '6px solid #4caf50',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(76, 175, 80, 0.2)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <InfoIcon sx={{ color: '#4caf50', fontSize: 32, mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    Merkregel
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.8 }}>
                    <strong>Ein Passwort – ein Konto:</strong> Verwenden Sie jedes Passwort nur einmal, nutzen Sie einen Passwort-Manager und aktivieren Sie überall 2FA.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Expandable section with icons */}
          <Box sx={{ mt: 4 }}>
            <Accordion
              defaultExpanded={false}
              sx={{
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#42a5f5' }} />}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff' }}>
                  Top 5: Wie Passwörter gestohlen werden
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3 }}>
                <List sx={{ color: '#ffffff' }}>
                  {credentialsItems.map((item, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        py: 2,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48, mr: 2 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, mb: 1.5 }}>
                              {item.description}
                            </Typography>
                            {item.whatToDo && (
                              <Box sx={{ 
                                mt: 1.5, 
                                pt: 1.5, 
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                borderRadius: 1,
                                p: 1.5,
                              }}>
                                <Typography variant="body2" sx={{ color: '#42a5f5', fontWeight: 600, mb: 0.5 }}>
                                  Was können Sie tun?
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                                  {item.whatToDo}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Paper>
      </Grid>
    );
  }

  // Special content for AI-Attacks section
  if (sectionId === 'ai-attacks') {
    const aiAttackMethods = [
      {
        icon: <VideocamIcon sx={{ color: '#f44336' }} />,
        title: 'Deepfakes: Gefälschte Videos und Audios',
        description: 'KI kann Stimmen und Gesichter täuschend echt nachahmen. Angreifer erstellen gefälschte Videoanrufe oder Sprachnachrichten, die wie echte Personen aussehen und klingen.',
        whatToDo: 'Seien Sie misstrauisch bei ungewöhnlichen Videoanrufen oder Sprachnachrichten, besonders wenn um Geld oder sensible Daten gebeten wird. Verifizieren Sie die Identität über einen anderen Kanal (z.B. direkter Anruf). Fragen Sie nach persönlichen Details, die nur die echte Person wissen sollte.'
      },
      {
        icon: <EmailIcon sx={{ color: '#2196f3' }} />,
        title: 'KI-generierte Phishing-Mails',
        description: 'Moderne KI kann perfekt formulierte, fehlerfreie Phishing-Mails erstellen, die schwer zu erkennen sind. Die Mails wirken professionell und enthalten keine Rechtschreibfehler mehr.',
        whatToDo: 'Prüfen Sie auch bei perfekt formulierten Mails die Absender-Adresse genau. Achten Sie auf verdächtige Links und ungewöhnliche Aufforderungen. Bei Unsicherheit kontaktieren Sie das Unternehmen direkt über offizielle Kanäle.'
      },
      {
        icon: <ImageIcon sx={{ color: '#ff9800' }} />,
        title: 'Gefälschte Bilder und "News"',
        description: 'KI kann realistische Bilder und "News" generieren, die schwer von echten zu unterscheiden sind. Diese werden für Desinformation und Manipulation genutzt.',
        whatToDo: 'Prüfen Sie Bilder und Nachrichten kritisch. Nutzen Sie Reverse-Image-Search, um die Quelle zu überprüfen. Vertrauen Sie nur seriösen Quellen und verifizieren Sie Informationen bei mehreren unabhängigen Stellen.'
      },
      {
        icon: <RecordVoiceOverIcon sx={{ color: '#9c27b0' }} />,
        title: 'Voice Cloning',
        description: 'Angreifer können mit wenigen Sekunden Audio Ihre Stimme nachahmen und Anrufe tätigen. Die gefälschte Stimme klingt täuschend echt.',
        whatToDo: 'Seien Sie vorsichtig bei Anrufen, die ungewöhnliche Anfragen stellen. Verifizieren Sie die Identität des Anrufers über einen Rückruf auf eine bekannte Nummer. Fragen Sie nach persönlichen Details, die nur die echte Person wissen sollte.'
      },
      {
        icon: <PsychologyIcon sx={{ color: '#4caf50' }} />,
        title: 'Automatisierte Social Engineering',
        description: 'KI analysiert Ihre Social-Media-Profile und erstellt personalisierte Angriffe. Die Nachrichten wirken, als kämen sie von bekannten Personen oder enthalten persönliche Informationen.',
        whatToDo: 'Seien Sie misstrauisch bei Nachrichten, die zu persönlich oder zu gut passend wirken. Prüfen Sie die Identität des Absenders über einen anderen Kanal. Teilen Sie nicht zu viele persönliche Informationen in sozialen Medien.'
      }
    ];

    const aiAttackRecognition = [
      {
        icon: <VisibilityIcon sx={{ color: '#00bcd4' }} />,
        title: 'Ungewöhnliche Qualität oder Unstimmigkeiten',
        description: 'Deepfakes können kleine Unstimmigkeiten haben: unnatürliche Bewegungen, fehlende Blinzelreflexe, unscharfe Gesichtskonturen oder unpassende Schatten.',
        whatToDo: 'Achten Sie auf Details: Blinzelt die Person natürlich? Passen die Bewegungen? Gibt es ungewöhnliche Schatten oder Unschärfen? Bei Videoanrufen: Prüfen Sie die Bildqualität und Synchronität von Ton und Bild.'
      },
      {
        icon: <ScheduleIcon sx={{ color: '#e91e63' }} />,
        title: 'Ungewöhnlicher Zeitpunkt oder Kontext',
        description: 'KI-Angriffe kommen oft zu ungewöhnlichen Zeiten oder in unerwarteten Situationen. Die Nachricht oder der Anruf passt nicht zum normalen Kommunikationsmuster.',
        whatToDo: 'Fragen Sie sich: Würde diese Person normalerweise so kommunizieren? Passt der Zeitpunkt? Gibt es einen Grund für diese ungewöhnliche Nachricht? Bei Zweifeln: Verifizieren Sie über einen anderen Kanal.'
      },
      {
        icon: <SecurityIcon sx={{ color: '#ff5722' }} />,
        title: 'Aufforderung zu ungewöhnlichen Handlungen',
        description: 'KI-generierte Angriffe fordern oft zu ungewöhnlichen Handlungen auf: Geldüberweisungen, Weitergabe von Codes, Öffnen von Links oder Installation von Software.',
        whatToDo: 'Seien Sie misstrauisch bei Aufforderungen, die ungewöhnlich sind oder unter Druck gesetzt werden. Seriöse Personen würden nie per Nachricht nach sensiblen Daten oder sofortigen Handlungen fragen. Nehmen Sie sich Zeit zum Nachdenken.'
      },
      {
        icon: <SearchIcon sx={{ color: '#795548' }} />,
        title: 'Fehlende Verifizierungsmöglichkeiten',
        description: 'Echte Kommunikation lässt sich immer über mehrere Kanäle verifizieren. Bei KI-Angriffen fehlen oft diese Verifizierungsmöglichkeiten oder sie werden aktiv verhindert.',
        whatToDo: 'Versuchen Sie, die Identität über einen anderen Kanal zu verifizieren (z.B. direkter Anruf, persönliches Treffen, andere Kommunikationsplattform). Wenn Verifizierung nicht möglich ist, sollten Sie misstrauisch sein.'
      },
      {
        icon: <WarningIcon sx={{ color: '#ffc107' }} />,
        title: 'Zu perfekt oder zu persönlich',
        description: 'KI-generierte Inhalte können manchmal zu perfekt wirken oder zu viele persönliche Details enthalten, die die KI aus öffentlichen Quellen gesammelt hat.',
        whatToDo: 'Fragen Sie sich: Wirkt die Nachricht zu gut, um wahr zu sein? Enthält sie zu viele persönliche Details, die eigentlich nicht öffentlich sein sollten? Bei Zweifeln: Verifizieren Sie die Identität und den Inhalt.'
      }
    ];

    return (
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Paper elevation={3} sx={{ 
          p: 4, 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderLeft: '6px solid #2196f3',
          color: '#ffffff', 
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
        }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
            KI als Angriffsinstrument: Gefälschte Inhalte und moderne Betrugsmethoden
          </Typography>
          
          {/* Centered video */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              component="video"
              src={aiAttacksVideo}
              controls
              sx={{
                width: '100%',
                maxWidth: '450px',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              }}
            />
          </Box>

          {/* Demo: Voice Cloning Beispiele */}
          {/* <Box sx={{ mb: 4 }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(0, 188, 212, 0.12) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
              }}
            >
              <Typography
                variant="h5"
                sx={{ color: '#ffffff', fontWeight: 700, mb: 2 }}
              >
                Demo: Voice Cloning
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    component="audio"
                    src=""
                    controls
                    sx={{ width: 220, maxWidth: '100%' }}
                  />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    component="audio"
                    src=""
                    controls
                    sx={{ width: 220, maxWidth: '100%' }}
                  />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    component="audio"
                    src=""
                    controls
                    sx={{ width: 220, maxWidth: '100%' }}
                  />
                </Box>
              </Box>
            </Paper>
          </Box> */}

          {/* Demo: KI-gestützte Telefonangriffe */}
          {/* <Box sx={{ mb: 4 }}>
            <Accordion
              defaultExpanded={false}
              sx={{
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#42a5f5' }} />}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: 2,
                  '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.15)' },
                  '& .MuiAccordionSummary-content': { margin: '16px 0' },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff' }}>
                  Demo: KI-Anrufe im Vergleich
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ color: '#ffffff', mb: 1 }}>
                      AI_MS
                    </Typography>
                    <Box
                      component="video"
                      src=""
                      controls
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        borderRadius: 2,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ color: '#ffffff', mb: 1 }}>
                      AI_MS_NL
                    </Typography>
                    <Box
                      component="video"
                      src=""
                      controls
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        borderRadius: 2,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box> */}
          
          {/* Merksatz über den Erkennungsmerkmalen */}
          <Box sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)',
              border: '2px solid rgba(76, 175, 80, 0.3)',
              borderLeft: '6px solid #4caf50',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(76, 175, 80, 0.2)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <InfoIcon sx={{ color: '#4caf50', fontSize: 32, mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    Merksatz
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.8 }}>
                    <strong>Stimme, Bild oder Nachricht:</strong> Vertrauen Sie nicht allein dem Eindruck – verifizieren Sie immer über einen zweiten, unabhängigen Kanal.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Wie wird KI für Angriffe genutzt */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Accordion
              defaultExpanded={false}
              sx={{
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#42a5f5' }} />}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff' }}>
                  Wie wird KI für Angriffe genutzt
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3 }}>
                <List sx={{ color: '#ffffff' }}>
                  {aiAttackMethods.map((item, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        py: 2,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48, mr: 2 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, mb: 1.5 }}>
                              {item.description}
                            </Typography>
                            {item.whatToDo && (
                              <Box sx={{ 
                                mt: 1.5, 
                                pt: 1.5, 
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                borderRadius: 1,
                                p: 1.5,
                              }}>
                                <Typography variant="body2" sx={{ color: '#42a5f5', fontWeight: 600, mb: 0.5 }}>
                                  Was können Sie tun?
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                                  {item.whatToDo}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Wie kann man KI-Angriffe erkennen */}
          <Box sx={{ mt: 4 }}>
            <Accordion
              defaultExpanded={false}
              sx={{
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#42a5f5' }} />}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff' }}>
                  Wie kann man KI-Angriffe erkennen
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3 }}>
                <List sx={{ color: '#ffffff' }}>
                  {aiAttackRecognition.map((item, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        py: 2,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48, mr: 2 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, mb: 1.5 }}>
                              {item.description}
                            </Typography>
                            {item.whatToDo && (
                              <Box sx={{ 
                                mt: 1.5, 
                                pt: 1.5, 
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                borderRadius: 1,
                                p: 1.5,
                              }}>
                                <Typography variant="body2" sx={{ color: '#42a5f5', fontWeight: 600, mb: 0.5 }}>
                                  Was können Sie tun?
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                                  {item.whatToDo}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Paper>
      </Grid>
    );
  }

  // Special content for Schock-Anrufe section
  if (sectionId === 'schock-anrufe') {
    const schockAnrufItems = [
      {
        icon: <WarningIcon sx={{ color: '#f44336' }} />,
        title: 'Emotionale Erpressung',
        description: 'Anrufer erzeugen Panik durch falsche Behauptungen (z.B. "Ihr Enkel ist verhaftet!", "Ihr Konto wurde gehackt!"). Sie nutzen Ihre Emotionen, um Sie zu unüberlegten Handlungen zu drängen.',
        whatToDo: 'Bewahren Sie Ruhe und lassen Sie sich nicht von Emotionen leiten. Legen Sie auf und verifizieren Sie die Situation über einen anderen Kanal. Kontaktieren Sie die betroffene Person direkt oder die echte Behörde über offizielle Nummern.'
      },
      {
        icon: <ScheduleIcon sx={{ color: '#ff9800' }} />,
        title: 'Dringlichkeit und Zeitdruck',
        description: 'Betrüger fordern sofortige Zahlung oder Handlung, ohne Zeit zum Nachdenken zu lassen. Sie erzeugen künstlichen Druck ("Nur noch 10 Minuten!", "Sofort handeln!").',
        whatToDo: 'Lassen Sie sich nicht unter Druck setzen. Seriöse Behörden geben Ihnen Zeit für wichtige Entscheidungen. Nehmen Sie sich die Zeit, die Situation zu prüfen und sich zu informieren. Bei echten Notfällen würden Sie über offizielle Kanäle kontaktiert.'
      },
      {
        icon: <GavelIcon sx={{ color: '#2196f3' }} />,
        title: 'Vortäuschung von Autorität',
        description: 'Anrufer geben sich als Polizei, Gerichtsvollzieher, Finanzamt oder andere Behörden aus. Sie nutzen offizielle Titel und Drohungen, um Glaubwürdigkeit zu erzeugen.',
        whatToDo: 'Echte Behörden würden nie am Telefon nach sofortigen Zahlungen fragen. Verifizieren Sie die Identität des Anrufers: Rufen Sie die Behörde direkt über die offizielle Nummer an (nicht die Nummer, die der Anrufer gibt). Behörden kommunizieren wichtige Angelegenheiten meist schriftlich.'
      },
      {
        icon: <BlockIcon sx={{ color: '#9c27b0' }} />,
        title: 'Verbot der Kontaktaufnahme mit Dritten',
        description: 'Betrüger verbieten, Familie, Freunde oder Behörden zu kontaktieren, um Aufklärung zu verhindern. Sie behaupten, dies würde die Situation verschlimmern.',
        whatToDo: 'Dies ist ein klares Warnsignal! Echte Behörden würden nie verbieten, sich zu informieren. Kontaktieren Sie sofort eine vertrauenswürdige Person oder die echte Behörde. Teilen Sie die Situation mit anderen, um eine objektive Einschätzung zu bekommen.'
      },
      {
        icon: <AccountBalanceWalletIcon sx={{ color: '#4caf50' }} />,
        title: 'Zahlung per Kryptowährung oder Geschenkkarten',
        description: 'Seriöse Behörden fordern niemals Zahlung per Bitcoin, iTunes-Karten, Amazon-Gutscheinen oder ähnlichen unverfolgbaren Zahlungsmethoden. Dies ist ein klares Zeichen für Betrug.',
        whatToDo: 'Zahlen Sie niemals per Kryptowährung oder Geschenkkarten, wenn Sie am Telefon danach gefragt werden. Echte Behörden nutzen offizielle Zahlungswege. Wenn Sie bereits gezahlt haben, kontaktieren Sie sofort Ihre Bank und die Polizei.'
      }
    ];

    return (
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Paper elevation={3} sx={{ 
          p: 4, 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderLeft: '6px solid #2196f3',
          color: '#ffffff', 
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
        }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
            Emotionale Erpressung: Schockanrufe erkennen und abwehren
          </Typography>
          
          {/* Centered video */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              component="video"
              src={schockAnrufVideo}
              controls
              sx={{
                width: '100%',
                maxWidth: '450px',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              }}
            />
          </Box>

          {/* YouTube Video Link */}
          {/* <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                maxWidth: '450px',
                width: '100%',
                background: 'linear-gradient(135deg, rgba(255, 0, 0, 0.1) 0%, rgba(200, 0, 0, 0.1) 100%)',
                border: '2px solid rgba(255, 0, 0, 0.3)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(255, 0, 0, 0.2)',
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                Zusätzliches Video
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="large"
                href="https://www.youtube.com/watch?v=F-zQ0wuaSN4"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(255, 0, 0, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Video auf YouTube ansehen
              </Button>
            </Paper>
          </Box> */}
          
          {/* Merkregel über den Erkennungsmerkmalen */}
          <Box sx={{ mt: 2 }}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)',
              border: '2px solid rgba(76, 175, 80, 0.3)',
              borderLeft: '6px solid #4caf50',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(76, 175, 80, 0.2)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <InfoIcon sx={{ color: '#4caf50', fontSize: 32, mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    Merkregel
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.8 }}>
                    <strong>Bei Schock am Telefon:</strong> Auflegen, durchatmen, selbst zurückrufen – niemals im Affekt zahlen oder Daten herausgeben.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Expandable section with icons */}
          <Box sx={{ mt: 4 }}>
            <Accordion
              defaultExpanded={false}
              sx={{
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#42a5f5' }} />}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff' }}>
                  Top 5: Typische Erkennungsmerkmale für Schockanrufe
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3 }}>
                <List sx={{ color: '#ffffff' }}>
                  {schockAnrufItems.map((item, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        py: 2,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48, mr: 2 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, mb: 1.5 }}>
                              {item.description}
                            </Typography>
                            {item.whatToDo && (
                              <Box sx={{ 
                                mt: 1.5, 
                                pt: 1.5, 
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                borderRadius: 1,
                                p: 1.5,
                              }}>
                                <Typography variant="body2" sx={{ color: '#42a5f5', fontWeight: 600, mb: 0.5 }}>
                                  Was können Sie tun?
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                                  {item.whatToDo}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Paper>
      </Grid>
    );
  }

  // Special content for Epilog section
  if (sectionId === 'epilog') {
    return (
      <ChapterLayout
        title="Fazit: Was wir aus Evelyns Geschichte lernen können"
        video={epilogVideo}
        noteTitle="Bewusstsein schärfen"
        noteText={
          <span>
            <strong>Informieren Sie sich regelmäßig:</strong> Verfolgen Sie aktuelle Betrugsmethoden und digitale Trends – und teilen Sie Ihr Wissen mit Familie, Freunden und Kolleg:innen. 
            Eine informierte Gesellschaft ist der beste Schutzschild: Je mehr Menschen die Maschen kennen, desto schwerer haben es Betrüger.
          </span>
        }
        accordionTitle="Schutzmaßnahmen im Überblick"
        accordionItems={[
          {
            title: 'Vorsicht bei Online-Schnäppchen',
            description: 'Prüfen Sie Shops auf Seriosität, bevor Sie bestellen. Achten Sie auf Impressum, AGB und Widerrufsbelehrung.'
          },
          {
            title: 'Phishing-Mails erkennen',
            description: 'Seien Sie misstrauisch bei dringenden Aufforderungen, ungewöhnlichen Absendern und Links in E-Mails.'
          },
          {
            title: 'Starke Passwörter verwenden',
            description: 'Nutzen Sie für jedes Konto ein einzigartiges, starkes Passwort und aktivieren Sie 2FA wo möglich.'
          },
          {
            title: 'KI-Fälschungen hinterfragen',
            description: 'Bei ungewöhnlichen Videoanrufen oder Sprachnachrichten: Verifizieren Sie die Identität des Absenders.'
          },
          {
            title: 'Schockanrufe nicht ernst nehmen',
            description: 'Echte Behörden fordern nie sofortige Zahlungen. Legen Sie auf und kontaktieren Sie die Behörde direkt.'
          },
          {
            title: 'Regelmäßige Updates',
            description: 'Halten Sie Ihre Software und Geräte auf dem neuesten Stand, um Sicherheitslücken zu schließen.'
          },
          {
            title: 'Bewusstsein schärfen',
            description: 'Informieren Sie sich regelmäßig über aktuelle Betrugsmethoden und teilen Sie Ihr Wissen mit anderen.'
          }
        ]}
      />
    );
  }

  // Default content for other sections
  return (
    <Grid item xs={12} sx={{ mb: 2 }}>
      <Paper elevation={3} sx={{ 
        p: 4, 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderLeft: '6px solid #2196f3',
        color: '#ffffff', 
        borderRadius: 2,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(10px)',
      }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 700, color: '#ffffff' }}>
          {sectionNumber}. {sectionLabel}
        </Typography>
        <Typography variant="body1" sx={{ color: '#ffffff', lineHeight: 1.8 }}>
          Hier kommt der Inhalt für {sectionLabel}...
        </Typography>
      </Paper>
    </Grid>
  );
};

// Main HackStory Component with TOC
const HackStoryLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // TEMPORARY: Make useAuth optional for public access
  const auth = useAuth();
  const user = auth?.user || null;
  
  // Determine active tab based on current route
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/hack-story' || path === '/hack-story/') {
      return 0; // Default to Intro
    }
    const section = sections.find(s => path.endsWith(s.path) || path.includes(`/${s.path}`));
    return section ? sections.indexOf(section) : 0;
  };

  const [tocValue, setTocValue] = useState(() => getActiveTab());

  // Update tab when route changes
  useEffect(() => {
    setTocValue(getActiveTab());
  }, [location.pathname]);

  // Get current section index
  const currentSectionIndex = getActiveTab();
  const hasNext = currentSectionIndex < sections.length - 1;
  const hasPrevious = currentSectionIndex > 0;

  // Navigate to next section
  const handleNext = () => {
    if (hasNext) {
      const nextSection = sections[currentSectionIndex + 1];
      navigate(`/hack-story/${nextSection.path}`);
    }
  };

  // Navigate to previous section
  const handlePrevious = () => {
    if (hasPrevious) {
      const prevSection = sections[currentSectionIndex - 1];
      navigate(`/hack-story/${prevSection.path}`);
    }
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
      minHeight: '100vh', 
      color: '#ffffff',
      position: 'relative',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }
    }}>
      {/* Table of Contents - Fixed at top */}
      <Box sx={{ 
        position: 'fixed', 
        top: 0, 
        left: 0,
        right: 0,
        zIndex: 1100, 
        width: '100%',
        background: 'transparent',
        pointerEvents: 'none', // Allow clicks to pass through to content below
      }}>
        <Container maxWidth="lg" sx={{ pointerEvents: 'auto', px: 2 }}>
          <Paper 
            elevation={2} 
            sx={{ 
              background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: { xs: 0, sm: 2 }, // No border radius on mobile for full width
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              overflow: 'visible',
              boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
              backdropFilter: 'blur(10px)',
              width: '100%',
            }}
          >
            <Tabs
              value={tocValue}
              onChange={(e, newValue) => {
                const section = sections[newValue];
                if (section) {
                  navigate(`/hack-story/${section.path}`);
                }
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                overflowX: 'auto', // Enable horizontal scrolling
                '& .MuiTab-root': {
                  minWidth: { xs: 100, sm: 120 }, // Smaller on mobile
                  textTransform: 'none',
                  fontWeight: 500,
                  color: '#ffffff',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Responsive font size
                  '&.Mui-selected': {
                    color: '#42a5f5',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#2196f3',
                },
                '& .MuiTabs-scrollButtons': {
                  color: '#ffffff',
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  }
                }
              }}
            >
              {sections.map((section) => (
                <Tab key={section.id} label={section.label} />
              ))}
            </Tabs>
          </Paper>
        </Container>
      </Box>

      {/* Spacer for fixed TOC */}
      <Box sx={{ height: { xs: '56px', sm: '64px' } }} />
      
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Zurück zum Lagebild Link */}
        <Box sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{
              color: '#42a5f5',
              '&:hover': {
                backgroundColor: 'rgba(66, 165, 245, 0.1)',
              },
            }}
          >
            Zurück zum Lagebild
          </Button>
        </Box>

        {/* Main Content */}
        <Grid container spacing={3}>
          {children}
        </Grid>
      </Container>

      {/* Navigation Buttons - Fixed bottom right */}
      <Box sx={{ 
        position: 'fixed',
        bottom: 100,
        right: 24,
        display: 'flex',
        gap: 1,
        zIndex: 1000,
      }}>
        {/* Previous Button */}
        {hasPrevious && (
          <Button
            variant="contained"
            onClick={handlePrevious}
            sx={{
              minWidth: 'auto',
              borderRadius: '50%',
              width: 56,
              height: 56,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
                boxShadow: '0 6px 20px rgba(33, 150, 243, 0.5)',
              },
            }}
            aria-label="Vorheriges Kapitel"
          >
            <ArrowBackIosIcon />
          </Button>
        )}
        
        {/* Next Button */}
        {hasNext && (
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{
              minWidth: 'auto',
              borderRadius: '50%',
              width: 56,
              height: 56,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #42a5f5 0%, #2196f3 100%)',
                boxShadow: '0 6px 20px rgba(33, 150, 243, 0.6)',
                transform: 'scale(1.05)',
              },
            }}
            aria-label="Nächstes Kapitel"
          >
            <ArrowForwardIcon />
          </Button>
        )}
      </Box>
    </Box>
  );
};

// Main HackStory Component with Routes
const HackStory = () => {
  const location = useLocation();
  
  // Redirect /hack-story to /hack-story/intro
  if (location.pathname === '/hack-story' || location.pathname === '/hack-story/') {
    return <Navigate to="/hack-story/intro" replace />;
  }

  return (
    <Routes>
      {sections.map((section) => (
        <Route
          key={section.id}
          path={section.path}
          element={
            <HackStoryLayout>
              <StorySection
                sectionId={section.id}
                sectionNumber={section.number}
                sectionLabel={section.label}
              />
            </HackStoryLayout>
          }
        />
      ))}
      {/* Default redirect to intro */}
      <Route path="*" element={<Navigate to="/hack-story/intro" replace />} />
    </Routes>
  );
};

export default HackStory;

